import type { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { FormEvent, useEffect, useRef, useState } from "react";
import { ArrowRight, Mail, Phone, X } from "lucide-react";
import {
  apiFetch,
  getFirebaseWebConfig,
  loginWithFirebasePhone,
  requestEmailOtp,
  verifyEmailOtp
} from "../lib/api";
import { useAppState } from "../state/AppState";

type Props = {
  open: boolean;
  onClose: () => void;
  onAuthenticated?: () => void;
};

const GOOGLE_ICON = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.6 14.6 2.8 12 2.8A9.2 9.2 0 0 0 2.8 12 9.2 9.2 0 0 0 12 21.2c5.3 0 8.8-3.7 8.8-8.9 0-.6-.1-1.1-.2-1.6H12Z" />
    <path fill="#4285F4" d="M3.8 7.7 7 10.1C7.9 7.9 9.7 6.3 12 6.3c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.6 14.6 2.8 12 2.8c-3.5 0-6.6 2-8.2 4.9Z" />
    <path fill="#FBBC05" d="M12 21.2c2.5 0 4.7-.8 6.3-2.2l-3-2.5c-.8.5-1.9.9-3.3.9-3.8 0-5.2-2.6-5.5-3.8l-3.1 2.4c1.5 3 4.7 5.2 8.6 5.2Z" />
    <path fill="#34A853" d="m3.4 16 3.1-2.4c-.2-.5-.3-1-.3-1.6s.1-1.1.3-1.6L3.4 8C2.9 9.1 2.8 10.5 2.8 12c0 1.4.2 2.8.6 4Z" />
  </svg>
);

function normalizePhoneForFirebase(value: string) {
  const compact = value.replace(/[\s()-]+/g, "");
  if (compact.startsWith("+")) return compact;

  const digits = compact.replace(/\D+/g, "");
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
  return compact;
}

function isFirebaseRuntimeError(error: unknown) {
  const code = typeof error === "object" && error && "code" in error ? String((error as { code?: unknown }).code || "") : "";
  return code.startsWith("auth/") || /recaptcha|captcha|firebase/i.test(error instanceof Error ? error.message : String(error || ""));
}

export function LoginModal({ onAuthenticated, open, onClose }: Props) {
  const { addToast, setUser } = useAppState();
  const [mode, setMode] = useState<"phone" | "email">("phone");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("+91 ");
  const [otp, setOtp] = useState("");
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const emailOtpRef = useRef<HTMLInputElement | null>(null);
  const phoneOtpRef = useRef<HTMLInputElement | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);
  const confirmationRef = useRef<ConfirmationResult | null>(null);

  const emailButtonLabel = emailOtpSent ? "Verify & Login" : "Send OTP";
  const phoneButtonLabel = phoneOtpSent ? "Verify & Login" : "Send OTP";

  function resetPhoneCaptcha() {
    confirmationRef.current = null;
    recaptchaRef.current?.clear();
    recaptchaRef.current = null;
  }

  useEffect(() => {
    if (!open) return;
    if (mode === "email" && emailOtpSent) emailOtpRef.current?.focus();
    if (mode === "phone" && phoneOtpSent) phoneOtpRef.current?.focus();
  }, [emailOtpSent, mode, open, phoneOtpSent]);

  if (!open) return null;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === "email") {
        if (!emailOtpSent) {
          if (!email.trim()) throw new Error("Enter your email first.");
          const response = await requestEmailOtp(email.trim());
          setEmailOtpSent(true);
          addToast("OTP sent", "success");
        } else {
          if (!/^\d{6}$/.test(otp.trim())) throw new Error("Enter the 6-digit OTP.");
          const nextUser = await verifyEmailOtp(email.trim(), otp.trim());
          setUser(nextUser);
          addToast("Login successful", "success");
          onAuthenticated?.();
        }
      } else {
        if (!phoneOtpSent) {
          const normalizedPhone = normalizePhoneForFirebase(phone);
          if (!/^\+\d{10,15}$/.test(normalizedPhone)) {
            throw new Error("Enter a valid phone number with country code.");
          }
          const firebaseResponse = await getFirebaseWebConfig();
          if (!firebaseResponse.enabled || !firebaseResponse.config) {
            throw new Error(firebaseResponse.detail || "Phone login is not configured.");
          }
          const [{ getApp, getApps, initializeApp }, { getAuth, RecaptchaVerifier, signInWithPhoneNumber }] = await Promise.all([
            import("firebase/app"),
            import("firebase/auth")
          ]);
          const app = getApps().length ? getApp() : initializeApp(firebaseResponse.config);
          const auth = getAuth(app);
          if (!recaptchaRef.current) {
            recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
              size: "invisible"
            });
          }
          try {
            confirmationRef.current = await signInWithPhoneNumber(auth, normalizedPhone, recaptchaRef.current);
          } catch (error) {
            resetPhoneCaptcha();
            throw error;
          }
          setPhoneOtpSent(true);
          setOtp("");
          addToast("OTP sent", "success");
        } else {
          if (!/^\d{6}$/.test(otp.trim())) throw new Error("Enter the 6-digit OTP.");
          if (!confirmationRef.current) throw new Error("Please request a new OTP.");
          let result;
          try {
            result = await confirmationRef.current.confirm(otp.trim());
          } catch (error) {
            resetPhoneCaptcha();
            setPhoneOtpSent(false);
            setOtp("");
            throw error;
          }
          const idToken = await result.user.getIdToken();
          const nextUser = await loginWithFirebasePhone(normalizePhoneForFirebase(phone), idToken);
          setUser(nextUser);
          addToast("Login successful", "success");
          onAuthenticated?.();
        }
      }
    } catch (error) {
      const message = mode === "phone" && isFirebaseRuntimeError(error)
        ? "Something went wrong. Try another login method."
        : error instanceof Error
          ? error.message
          : "Login failed";
      addToast(message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    try {
      const response = await apiFetch<{ url: string }>("/users/login/google/init");
      if (!response.url) throw new Error("Google login is not configured.");
      window.location.assign(response.url);
    } catch (error) {
      addToast(error instanceof Error ? error.message : "Google login failed", "error");
    }
  }

  function switchMode(nextMode: "phone" | "email") {
    setMode(nextMode);
    setOtp("");
    setEmailOtpSent(false);
    setPhoneOtpSent(false);
    if (mode === "phone") resetPhoneCaptcha();
    confirmationRef.current = null;
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="modal-panel login-modal-panel" role="dialog" aria-modal="true" aria-labelledby="login-title">
        <button className="modal-close login-modal-close" type="button" aria-label="Close Login" onClick={onClose}>
          <X size={20} aria-hidden="true" />
        </button>
        <div className="login-modal-layout">
          <div className="login-modal-form">
            <div className="login-modal-brand" translate="no">PROTO<span>DECCAN</span></div>
            <div className="login-container">
              <div className="login-copy">
                <p className="login-subtitle">Access quotes, orders, and profile details from one place.</p>
              </div>

              <button className="btn btn-google login-social-button" type="button" onClick={handleGoogleLogin}>
                <span className="login-social-icon">{GOOGLE_ICON}</span>
                Continue with Google
              </button>

              <div className="login-divider">or</div>

              <div className="login-tabs" role="tablist" aria-label="Login Method">
                <button type="button" className={mode === "phone" ? "login-tab active" : "login-tab"} onClick={() => switchMode("phone")} aria-selected={mode === "phone"}>
                  <Phone size={16} aria-hidden="true" />
                  <span>Phone OTP</span>
                </button>
                <button type="button" className={mode === "email" ? "login-tab active" : "login-tab"} onClick={() => switchMode("email")} aria-selected={mode === "email"}>
                  <Mail size={16} aria-hidden="true" />
                  <span>Email OTP</span>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {mode === "phone" ? (
                  <div id="login-phone-panel" className="login-panel">
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input className="form-control login-input" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210" value={phone} onChange={(event) => setPhone(event.currentTarget.value)} />
                    </div>
                    <div className={phoneOtpSent ? "form-group" : "form-group hidden"} id="login-phone-otp-group">
                      <label>Enter 6-digit OTP</label>
                      <input ref={phoneOtpRef} className="form-control login-input" name="otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(event) => setOtp(event.currentTarget.value.replace(/\D+/g, "").slice(0, 6))} />
                    </div>
                    <button className={`btn btn-primary btn-block login-submit${phoneOtpSent ? " hidden" : ""}`} id="btn-send-phone-otp" type="submit" disabled={loading}>
                      <span>{loading && !phoneOtpSent ? "Sending..." : "Send OTP"}</span>
                      <ArrowRight size={18} aria-hidden="true" />
                    </button>
                    <button className={`btn btn-primary btn-block login-submit${phoneOtpSent ? "" : " hidden"}`} id="btn-verify-phone-otp" type="submit" disabled={loading}>
                      <span>{loading && phoneOtpSent ? "Verifying..." : phoneButtonLabel}</span>
                      <ArrowRight size={18} aria-hidden="true" />
                    </button>
                    {!phoneOtpSent ? <p className="login-note">We send a one-time code to your phone.</p> : null}
                    <div id="recaptcha-container"></div>
                  </div>
                ) : (
                  <div id="login-email-panel" className="login-panel">
                    <div className="form-group">
                      <label>Email</label>
                      <input className="form-control login-input" name="email" type="email" autoComplete="email" spellCheck={false} placeholder="you@example.com" value={email} onChange={(event) => setEmail(event.currentTarget.value)} />
                    </div>
                    <div className={emailOtpSent ? "form-group" : "form-group hidden"} id="login-otp-group">
                      <label>Enter 6-digit OTP sent to email</label>
                      <input ref={emailOtpRef} className="form-control login-input" name="otp" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={otp} onChange={(event) => setOtp(event.currentTarget.value.replace(/\D+/g, "").slice(0, 6))} />
                    </div>
                    <button className="btn btn-primary btn-block login-submit" type="submit" disabled={loading}>
                      <span>{loading ? (emailOtpSent ? "Verifying..." : "Sending...") : emailButtonLabel}</span>
                      <ArrowRight size={18} aria-hidden="true" />
                    </button>
                    {!emailOtpSent ? <p className="login-note">We send a one-time code to your inbox.</p> : null}
                  </div>
                )}
              </form>
            </div>
          </div>

          <aside className="login-modal-visual" aria-hidden="true">
            <div className="login-modal-visual-card">
              <div className="login-modal-visual-copy">
                <span>Instant Quotes</span>
                <strong>Upload your models, track your orders, get delivered without hurdles</strong>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
